/**
 * Dashboard Rings Types
 *
 * Types for the Whoop-style performance rings on the dashboard.
 * Three rings: Doorstroom (Velocity), Vandaag (Flow), Liquiditeit (Liquidity)
 *
 * SCOPE: Sales-only dealerships. Garage metrics are OUT OF SCOPE for v1.
 */

// ============================================================================
// Dashboard Settings (stored in teams/{teamId}/settings/dashboardConfig)
// ============================================================================

/**
 * Business days configuration
 * Array of weekday numbers: 1=Monday, 2=Tuesday, ..., 7=Sunday
 */
export type BusinessDays = number[];

/**
 * Dashboard ring settings for a team
 * Controls thresholds and timing for ring calculations
 */
export interface DashboardRingSettings {
  /**
   * Days after which a vehicle is considered "stale" (aging)
   * Vehicles older than this start losing health score
   * Default: 30 days
   */
  staleVehicleDays: number;

  /**
   * Days after which a vehicle is considered "critical"
   * Vehicles at or beyond this age have 0% health
   * Default: 90 days
   */
  criticalVehicleDays: number;

  /**
   * Ideal number of days to collect payment after invoice sent
   * Invoices within this window have 100% health
   * Default: 7 days
   */
  idealCollectionDays: number;

  /**
   * Days after quote sent before follow-up task is created
   * Only triggers on business days
   * Default: 3 days
   */
  quoteFollowUpDays: number;

  /**
   * Business days (open days) for the dealership
   * Array of weekday numbers: 1=Monday, 2=Tuesday, ..., 7=Sunday
   * Default: [1, 2, 3, 4, 5] (Mon-Fri)
   */
  businessDays: BusinessDays;
}

/**
 * Default dashboard ring settings
 */
export const DEFAULT_DASHBOARD_RING_SETTINGS: DashboardRingSettings = {
  staleVehicleDays: 30,
  criticalVehicleDays: 90,
  idealCollectionDays: 7,
  quoteFollowUpDays: 3,
  businessDays: [1, 2, 3, 4, 5], // Mon-Fri
};

// ============================================================================
// Ring Score Types
// ============================================================================

/**
 * Ring color state based on score
 */
export type RingColor = 'green' | 'orange' | 'red';

/**
 * Individual ring score with metadata
 */
export interface RingScore {
  /** Ring identifier */
  id: 'doorstroom' | 'vandaag' | 'liquiditeit';

  /** Display label (Dutch) */
  label: string;

  /** Score percentage (0-100) */
  score: number;

  /** Color state based on thresholds */
  color: RingColor;
}

/**
 * Color thresholds for rings
 */
export const RING_COLOR_THRESHOLDS = {
  green: 85, // >= 85 is green
  orange: 60, // >= 60 and < 85 is orange
  // < 60 is red
} as const;

/**
 * Get ring color based on score
 */
export function getRingColor(score: number): RingColor {
  if (score >= RING_COLOR_THRESHOLDS.green) return 'green';
  if (score >= RING_COLOR_THRESHOLDS.orange) return 'orange';
  return 'red';
}

// ============================================================================
// Explanation Item Types (for "Explain the Orange")
// ============================================================================

/**
 * Action type for explanation items
 */
export type ExplanationActionType =
  | 'adjust_price' // Vehicle: adjust price
  | 'send_reminder' // Invoice: send payment reminder
  | 'follow_up' // Quote: follow up with customer
  | 'send_invoice' // Draft invoice: send it
  | 'view_vehicle' // View vehicle details
  | 'view_invoice' // View invoice details
  | 'view_quote'; // View quote details

/**
 * Explanation item for why a ring is orange/red
 * Format: [Item] → [Impact] → [Primary Action]
 */
export interface ExplanationItem {
  /** Unique identifier */
  id: string;

  /** Ring this explanation belongs to */
  ringId: 'doorstroom' | 'vandaag' | 'liquiditeit';

  /** Item description (e.g., "Audi A3 (42 dagen)") */
  item: string;

  /** Impact description (e.g., "-12% Doorstroom") */
  impact: string;

  /** Impact value for sorting (higher = more impactful) */
  impactValue: number;

  /** Primary action type */
  actionType: ExplanationActionType;

  /** Action label (Dutch) */
  actionLabel: string;

  /** Navigation href for action */
  actionHref: string;

  /** Related entity ID (vehicle, invoice, etc.) */
  entityId: string;

  /** Entity type */
  entityType: 'vehicle' | 'salesInvoice' | 'quote' | 'task';

  /** Current status (for status dropdown) */
  currentStatus?: string;

  /** Available status options (for status dropdown) */
  statusOptions?: { value: string; label: string }[];

  /** Whether this item can be snoozed */
  canSnooze?: boolean;

  /** Whether this item can be dismissed */
  canDismiss?: boolean;

  /** Whether this item is snoozed until a date */
  snoozedUntil?: string;

  /** Whether this item is permanently dismissed */
  isDismissed?: boolean;

  /** Task scope for task items: 'team' or 'user' */
  taskScope?: 'team' | 'user';

  /** Vehicle status for Doorstroom items */
  vehicleStatus?: 'available' | 'reserved' | 'sold' | 'purchased' | 'maintenance';

  /** Party email for Liquiditeit items (for email flow) */
  partyEmail?: string;

  /** Party phone for Liquiditeit items (for WhatsApp flow) */
  partyPhone?: string;

  /** Party name for Liquiditeit items (for email flow) */
  partyName?: string;

  /** Invoice number for Liquiditeit items (for email flow) */
  invoiceNo?: string;

  /** Party/customer ID for Liquiditeit items (for drawer flows) */
  partyId?: string;
}

// ============================================================================
// Predictive Nudge Types ("Coming Up")
// ============================================================================

/**
 * Predictive nudge for future risks
 * These do NOT affect current score but warn about upcoming issues
 */
export interface PredictiveNudge {
  /** Unique identifier */
  id: string;

  /** Ring this nudge relates to */
  ringId: 'doorstroom' | 'vandaag' | 'liquiditeit';

  /** Message (Dutch) */
  message: string;

  /** Number of items affected */
  count: number;

  /** Days until this becomes a problem */
  daysUntil: number;

  /** Navigation href for action */
  actionHref?: string;

  /** Entity ID for deep-linking (e.g., invoiceId for single-item nudges) */
  entityId?: string;

  /** Entity type for deep-linking */
  entityType?: 'vehicle' | 'salesInvoice' | 'quote' | 'task';
}

// ============================================================================
// Flow Task Types (for Vandaag ring)
// ============================================================================

/**
 * Flow task source - automated or manual
 */
export type FlowTaskSource = 'auto_invoice_to_send' | 'auto_quote_follow_up' | 'manual';

/**
 * Flow task status
 */
export type FlowTaskStatus = 'open' | 'completed' | 'cancelled' | 'dismissed';

/**
 * Flow task for Vandaag ring calculation
 * Automated tasks are computed dynamically, not persisted
 */
export interface FlowTask {
  /** Unique identifier */
  id: string;

  /** Task source */
  source: FlowTaskSource;

  /** Task title */
  title: string;

  /** Task description */
  description?: string;

  /** Due date (ISO string) */
  dueAt: string;

  /** Status */
  status: FlowTaskStatus;

  /** Completed at (ISO string) */
  completedAt?: string;

  /** Related entity ID */
  entityId?: string;

  /** Entity type */
  entityType?: 'salesInvoice' | 'quote' | 'vehicle' | 'customer';

  /** For quote follow-up: has user dismissed this? */
  dismissed?: boolean;

  /** For quote follow-up: quote sent at (ISO string) */
  quoteSentAt?: string;
}

// ============================================================================
// Dashboard Data Types
// ============================================================================

/**
 * Complete dashboard rings data
 */
export interface DashboardRingsData {
  /** Timestamp when data was computed */
  computedAt: string;

  /** Ring scores */
  rings: {
    doorstroom: RingScore;
    vandaag: RingScore;
    liquiditeit: RingScore;
  };

  /** Explanation items (top 3 per ring) */
  explanations: ExplanationItem[];

  /** Predictive nudges */
  nudges: PredictiveNudge[];

  /** Settings used for calculation */
  settings: DashboardRingSettings;
}
